import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatAngularModule } from '../../shared/modules/mat-angular.module';
import { Subscription } from 'rxjs';
import { ProjectManagementService, Project } from '../../services/project-management.service';
import { CountryService, DEFAULT_COUNTRIES } from '../../services/country.service';
import { GenericCommonTabsComponent, TabsConfiguration, TabData } from '../../shared/components/template-ai-common/generic-common-tabs/generic-common-tabs.component';
import { GenericCommonFilterComponent } from '../../shared/components/template-ai-common/generic-common-filter/generic-common-filter.component';
import { FilterConfig, FilterData, SelectedBrand } from '../../shared/models/generic-models';
import {
  GenericTemplateCommonService,
  BULK_REMAINING_TABS_SESSION_KEY,
} from '../../shared/components/template-ai-common/services/generic-template-common.service';
import { ProjectHistoryService } from '../../services/project-history.service';
import { Router } from '@angular/router';
import { AnalysisStatusService } from '../../services/analysis-status.service';

@Component({
  selector: 'app-generic-template-ai',
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,
    GenericCommonTabsComponent,
    GenericCommonFilterComponent,
  ],
  templateUrl: './generic-template-ai.component.html',
  styleUrl: './generic-template-ai.component.scss',
  standalone: true,
})
export class GenericTemplateAiComponent implements OnInit, OnDestroy {
  @Input() projectId: string | null = null;
  @Input() projectdataId: string | null = null;

  // Filter configuration for generic brand analysis
  filterConfig: FilterConfig = {
    entityType: 'brand',
    entityLabel: 'Brand',
    entityPlural: 'brands',
    countryLabel: 'Country',
    countryOptions: [...DEFAULT_COUNTRIES],
    searchPlaceholder: 'Search brands...',
    buttonText: 'Analyze Brands'
  };

  // Tabs configuration for generic brand analysis
  tabsConfig: TabsConfiguration = {
    entityType: 'brand',
    entityLabel: 'Brand',
    entityPlural: 'brands',
    defaultTab: 'overview',
    tabs: [
      { id: 'overview', name: 'Overview', icon: 'dashboard', component: 'overview', reloadable: true },
      // { id: 'ai-visibility', name: 'AI Visibility', icon: 'visibility', component: 'ai-visibility', reloadable: true }, // Hidden for now
      { id: 'ai-citations', name: 'AI Citations', icon: 'format_list_numbered', component: 'ai-citations', enhanced: true, reloadable: true },
      { id: 'category-analysis', name: 'Category Analysis', icon: 'category', component: 'category', reloadable: true },
      { id: 'prompts', name: 'Prompts', icon: 'quiz', component: 'prompts', reloadable: false },
      { id: 'consumer-insights', name: 'Consumer Insights', icon: 'psychology', component: 'consumer-insights', reloadable: true },
      { id: 'sentiment-tracking', name: 'Sentiment Tracking', icon: 'sentiment_satisfied', component: 'sentiment', enhanced: true, reloadable: true },
      // { id: 'area-comparison', name: 'Course Comparison', icon: 'compare_arrows', component: 'area-comparison', visualizationTypes: ['radar', 'bar', 'cards'], enhanced: true, reloadable: true },
      { id: 'ai-content-index', name: 'AI Content Index', icon: 'article', component: 'ai-content-index', enhanced: true, reloadable: true },
      { id: 'prompt-research', name: 'Prompt Creation', icon: 'search', component: 'prompt-research', reloadable: true },
      { id: 'content-creation', name: 'Content Creation', icon: 'create', component: 'content-creation', reloadable: true },
      { id: 'performance-tracking', name: 'Performance Tracking', icon: 'trending_up', component: 'performance-tracking', reloadable: true },
      { id: 'prompt-tracking', name: 'Prompt Tracking', icon: 'track_changes', component: 'prompt-tracking', reloadable: true },
      { 
        id: 'audit', 
        name: 'Audit', 
        icon: 'verified', 
        component: 'audit', 
        reloadable: true,
        subTabs: [
          { id: 'eeat-audit', name: 'EEAT Audit', icon: 'verified', component: 'eeat-audit', reloadable: true },
          { id: 'landing-page-audit', name: 'Landing Page Audit', icon: 'web', component: 'landing-page-audit', reloadable: true },
          { id: 'ai-audit', name: 'AI Audit', icon: 'analytics', component: 'ai-audit', reloadable: true }
        ]
      },
      { id: 'glossary', name: 'Glossary', icon: 'menu_book', component: 'glossary', reloadable: false },
      // { id: 'methodology', name: 'Our Methodology', icon: 'science', component: 'methodology', reloadable: true },
    ],
    tableColumns: [
      { key: 'brand', label: 'Brand', type: 'text' },
      { key: 'composite_score', label: 'Composite Score', type: 'number' },
      { key: 'sentiment', label: 'Sentiment', type: 'number' },
      { key: 'market_share', label: 'Market Share', type: 'percentage' },
      { key: 'brand_awareness', label: 'Brand Awareness', type: 'percentage' },
      { key: 'customer_satisfaction', label: 'Customer Satisfaction', type: 'percentage' },
      { key: 'innovation_score', label: 'Innovation Score', type: 'number' }
    ],
    displayedColumns: ['brand', 'composite_score', 'sentiment', 'market_share', 'brand_awareness', 'customer_satisfaction', 'innovation_score'],
    chartMetrics: ['composite_score', 'sentiment', 'market_share', 'brand_awareness'],
    maxEntities: 5
  };

  // Results
  table: any[] = [];
  chart: any | null = null;

  // UI
  isLoading = false;
  /** True by default when on a version page; set false when GET /generic/history/{id} returns data or error */
  isHistoryLoading = true;
  isInitialLoading = true; // For showing global loader on page load
  error: string | null = null;

  // Task-based processing
  hasSearched: boolean = false;

  // Selected entities for tabs
  selectedEntities: SelectedBrand[] = [];
  category: string = 'Technology';
  country: string = 'Global';
  hardReload: boolean = false; // Store hard reload flag from filter
  currentActiveTab: string = 'overview'; // Track current active tab
  private isBulkAnalysisInProgress: boolean = false; // Track if bulk analysis is in progress

  @ViewChild(GenericCommonTabsComponent) commonTabsComponent!: GenericCommonTabsComponent;
  @ViewChild(GenericCommonFilterComponent) commonFilterComponent!: GenericCommonFilterComponent;

  // Route-based preselection
  preselectedBrand: string | null = null;

  // Project data
  projectData: Project | null = null;
  currentVersionName: string | null = null; // Current version name (e.g., v1, v2) from history

  // Subscriptions
  private subscriptions: Subscription[] = [];
  private readonly invalidPrimaryBrandNames = new Set(['your brand', 'your_brand', 'brand', 'the brand']);
  private hasTriggeredPostBackgroundReload = false;

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private projectManagementService: ProjectManagementService,
    private route: ActivatedRoute,
    private countryService: CountryService,
    private projectHistoryService: ProjectHistoryService,
    private router: Router,
    private analysisStatusService: AnalysisStatusService
  ) { }

  ngOnInit(): void {
    // Set the tab flow to brand-analysis for generic flow
    this.templateCommonService.updateTabFlow({ type: 'brand-analysis', activeTab: 'overview' });

    // When on a version/history page, show loader until API returns data or error
    this.isHistoryLoading = !!this.projectdataId;

    // Initialize data first (config API)
    this.loadInitialData();

    // Subscribe to service observables
    this.subscribeToServiceObservables();

    // Then load project data (if projectId exists)
    if (this.projectId) {
      if (this.projectdataId) {
        // Previous-Analysis flow: Load project first, then history
        this.loadProjectDataForHistory().then(() => {
          // After project loads, load history
          this.loadProjectDataFromHistory();
        });
      } else {
        // Re-Analysis flow: Just load project (force refresh to bypass cache)
        this.hardReload = true; // Set hard reload flag for Re-Analysis
        // Initialize global loader immediately for Re-Analysis (before project API call)
        // This ensures loader shows right away while project data is being fetched
        this.templateCommonService.initializeGlobalLoaderForBulkAnalysis();
        this.loadProjectData();
      }
    } else {
      // Check for brand query parameter (existing functionality)
      this.route.queryParams.subscribe(params => {
        this.preselectedBrand = params['brand'] ? decodeURIComponent(params['brand']) : null;
      });
    }

    // Hide initial loader after a short delay to show the global loader
    setTimeout(() => {
      this.isInitialLoading = false;
    }, 1500);
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private subscribeToServiceObservables(): void {
    // Subscribe to analysis response
    const analysisSub = this.templateCommonService.analysisResponse$.subscribe(response => {
      if (response) {
        // console.log('Generic Template AI - Received analysis response:', response);
        this.table = response.table || [];
        this.chart = response.chart || null;
        // console.log('Generic Template AI - Updated table and chart data');
      }
    });

    // Subscribe to loading state
    const loadingSub = this.templateCommonService.isLoading$.subscribe(loading => {
      this.isLoading = loading;
    });

    // Subscribe to error state
    const errorSub = this.templateCommonService.error$.subscribe(error => {
      this.error = error;
    });

    // Subscribe to analysis completion
    const analysisCompletedSub = this.templateCommonService.analysisCompleted$.subscribe(completed => {
      if (completed) {
        console.log('🎉 Analysis completed, refreshing current tab if needed');
        this.refreshCurrentTabAfterAnalysis();
        
        // Reset bulk analysis flag
        this.isBulkAnalysisInProgress = false;
      }
    });

    // Subscribe to LLM comparison complete - distribute full version data to all tabs (avoids partial merge that blanks other tabs)
    const llmCompleteSub = this.templateCommonService.llmComparisonCompleteWithData$.subscribe(versionData => {
      if (versionData?.data) {
        console.log('📦 LLM comparison completed - distributing version data to all tabs');
        const allTabsData = versionData.data;
        const filters = versionData.filters || {};
        this.setupFilterDataFromHistory(filters, versionData);
        this.distributeHistoryDataToTabs(allTabsData, filters);
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, loadingSub, errorSub, analysisCompletedSub, llmCompleteSub);
  }

  private loadProjectData(): void {
    if (!this.projectId) return;

    const projectSub = this.projectManagementService.getProjectById(this.projectId).subscribe({
      next: (project: Project) => {
        console.log('Project data loaded:', project);
        this.projectData = project;
        
        // Set country from project's primary_country first
        if (project.primary_country) {
          this.country = project.primary_country;
          console.log('Set country from project:', this.country);
          // The country will be passed to filter component via [initialCountry] binding
          // Angular change detection will handle the update automatically
        }
        // Set category from project's primary category (used by bulk-search and filter data)
        const projectCategory = project.category ?? (project.categories?.find((c) => c.isPrimary)?.name);
        if (projectCategory) {
          this.category = projectCategory;
          console.log('Set category from project:', this.category);
        }
        // Extract brand name from project name for preselection
        // This should happen after country is set so filter component uses correct country
        this.preselectedBrand = project.name;
        console.log('Set preselectedBrand to:', this.preselectedBrand);
        
        // Seed selectedEntities from project; include domain for brand (description) and competitors (url hostname) for filter tooltips
        const competitors: any[] = (project.competitors || []).slice(0, 5);
        const primaryName = project.name;
        const brandDomain = (project.description || '').trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '') || undefined;
        this.selectedEntities = [
          { id: 1, name: primaryName, country: this.country, domain: brandDomain || project.description },
          ...competitors.map((c, i) => ({
            id: i + 2,
            name: c.name || '',
            country: this.country,
            domain: this.extractDomainFromCompetitorUrl(c.url) || undefined,
            ...(c.url && { url: c.url })
          }))
        ];
        
        // Update project data in the service so all tabs can access it
        this.templateCommonService.updateProjectData(project);
        
        // Set project context so LLM comparison metrics and other APIs include project_id (Re-Analysis flow)
        this.templateCommonService.setProjectContext(this.projectId, this.projectdataId);

        // Before starting a new bulk search, check if a task is already running (e.g. user refreshed during Re-Analysis)
        this.analysisStatusService.checkActiveAnalysis().subscribe({
          next: (status) => {
            const active = status?.has_active_analysis && status?.project_id === this.projectId && status?.status !== 'cancelled';
            if (active && status?.task_id) {
              this.isBulkAnalysisInProgress = true;
              if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(BULK_REMAINING_TABS_SESSION_KEY)) {
                    let remainingTaskId = status.task_id as string;
                    try {
                      const raw = sessionStorage.getItem(BULK_REMAINING_TABS_SESSION_KEY);
                      if (raw) {
                        const parsed = JSON.parse(raw) as { taskId?: string };
                        if (parsed?.taskId) {
                          remainingTaskId = parsed.taskId;
                        }
                      }
                    } catch {
                      // Fall back to active status task_id.
                    }
                    // Phase-2 task exists; resume silent remaining-tabs loader instead of returning idle.
                    this.templateCommonService.startSilentRemainingTabsPolling(remainingTaskId, (st) => {
                      if (st.status === 'completed' && this.projectdataId) {
                        this.projectHistoryService.getVersionData(this.projectdataId).subscribe({
                          next: (vd) => {
                            const data = vd?.data;
                            if (data) {
                              this.distributeHistoryDataToTabs(data, vd.filters || {});
                            }
                            this.triggerPostBackgroundBulkReload();
                          },
                          error: () => this.triggerPostBackgroundBulkReload(),
                        });
                      } else if (st.status === 'completed') {
                        this.triggerPostBackgroundBulkReload();
                      }
                    });
                    this.hasSearched = true;
                return;
              }
              console.log('🔄 Active bulk analysis task found – resuming progress instead of starting a new one');
              const totalTabs = (status as any)?.total_tabs ?? 9;
              const filterData: FilterData = {
                selectedEntities: this.selectedEntities.length > 0 ? this.selectedEntities : [{ id: 1, name: project.name, country: this.country, domain: project.description }],
                category: this.category,
                country: this.country,
                projectCreatedAt: project.created_at,
                hardReload: this.hardReload || false
              };
              this.templateCommonService.resumeBulkAnalysisProgress(
                status.task_id,
                this.projectId ?? undefined,
                this.projectdataId ?? undefined,
                totalTabs,
                filterData
              );
              this.hasSearched = true;
              return;
            }
            // No active task – start bulk search as usual
            this.autoTriggerAnalysisWithProject(project);
          },
          error: () => this.autoTriggerAnalysisWithProject(project)
        });
      },
      error: (err: any) => {
        console.error("Failed to load project:", {
          status: err?.status,
          statusText: err?.statusText,
          message: err?.message,
          projectId: this.projectId
        });
        this.error = `Failed to load project: ${err?.status} ${err?.statusText}`;
      }
    });

    // Add to subscriptions for cleanup
    this.subscriptions.push(projectSub);
  }

  private loadProjectDataFromHistory(): void {
    if (!this.projectdataId) return;

    this.isLoading = true;
    this.isHistoryLoading = true;
    this.error = null;

    // Set project context so LLM comparison metrics and other APIs include project_id/projectdata_id
    this.templateCommonService.setProjectContext(this.projectId, this.projectdataId);

    this.projectHistoryService.getVersionData(this.projectdataId).subscribe({
      next: (versionData) => {
        console.log('📦 Loaded project data from history:', versionData);
        
        // Store current version name for display
        this.currentVersionName = versionData.name || null;
        
        // Extract all tabs data from versionData.data
        const allTabsData = versionData.data;
        
        // Extract filters from versionData (if available)
        const filters = versionData.filters || {};
        
        // Set up filter data from history filters FIRST (before distributing data)
        // Project data should already be loaded at this point
        this.setupFilterDataFromHistory(filters, versionData);
        
        // Update filter data with project info if needed
        if (this.projectData) {
          const currentFilterData = this.templateCommonService.getCurrentFilterData();
          if (currentFilterData) {
            // Ensure projectCreatedAt is set
            if (!currentFilterData.projectCreatedAt) {
              const updatedFilterData: FilterData = {
                ...currentFilterData,
                projectCreatedAt: this.projectData.created_at
              };
              this.templateCommonService.updateFilterData(updatedFilterData);
            }
          } else {
            // If no filter data was set, create it from project
            // Extract domain from project description if available
            const projectDescription = this.projectData?.description || '';
            let baseDomain: string | null = null;
            try {
              // Access extractBaseDomain method (now public)
              baseDomain = (this.templateCommonService as any).extractBaseDomain(projectDescription);
            } catch (e) {
              console.warn('Could not extract domain from project description:', e);
            }
            
            const filterData: FilterData = {
              selectedEntities: this.selectedEntities,
              category: this.category || 'General',
              country: this.country || 'Global',
              projectCreatedAt: this.projectData.created_at,
              base_domain: baseDomain || undefined,
              description: projectDescription || undefined
            };
            this.templateCommonService.updateFilterData(filterData);
          }
        }
        
        // Clear any existing errors before distributing data
        this.templateCommonService.setError(null);
        
        // Distribute data to tabs via service (after filter data is set)
        this.distributeHistoryDataToTabs(allTabsData, filters);

        try {
          const raw = sessionStorage.getItem(BULK_REMAINING_TABS_SESSION_KEY);
          if (raw && this.projectdataId) {
            const parsed = JSON.parse(raw) as {
              taskId: string;
              projectdataId: string;
              total?: number;
            };
            if (parsed.projectdataId === this.projectdataId && parsed.taskId) {
              sessionStorage.removeItem(BULK_REMAINING_TABS_SESSION_KEY);
              this.templateCommonService.startSilentRemainingTabsPolling(
                parsed.taskId,
                (st) => {
                  if (st.status === 'completed' && this.projectdataId) {
                    this.projectHistoryService.getVersionData(this.projectdataId).subscribe({
                      next: (vd) => {
                        const data = vd?.data;
                        if (data) {
                          this.distributeHistoryDataToTabs(data, vd.filters || {});
                        }
                        this.triggerPostBackgroundBulkReload();
                      },
                      error: () => this.triggerPostBackgroundBulkReload(),
                    });
                  } else if (st.status === 'completed') {
                    this.triggerPostBackgroundBulkReload();
                  }
                }
              );
            }
          }
        } catch {
          /* ignore */
        }
        // Fallback: even without session key, ask backend for active analysis and resume other-tabs bulk if running.
        this.templateCommonService.resumeBackgroundTabsFromActiveStatus(
          this.projectId || undefined,
          this.projectdataId || undefined,
          (st) => {
            if (st.status === 'completed' && this.projectdataId) {
              this.projectHistoryService.getVersionData(this.projectdataId).subscribe({
                next: (vd) => {
                  const data = vd?.data;
                  if (data) {
                    this.distributeHistoryDataToTabs(data, vd.filters || {});
                  }
                  this.triggerPostBackgroundBulkReload();
                },
                error: () => this.triggerPostBackgroundBulkReload(),
              });
            } else if (st.status === 'completed') {
              this.triggerPostBackgroundBulkReload();
            }
          }
        );
        
        // First, check for active LLM comparison metrics task (might be running from before)
        this.templateCommonService.checkAndResumeActiveLLMComparisonMetricsTask();
        
        // Then check if LLM comparison metrics are missing and trigger calculation
        // Check both raw overview data AND distributed data (after extraction)
        const rawOverviewData = allTabsData.overview;
        const distributedOverviewData = this.templateCommonService.getCurrentAnalysisResponse();
        
        // Comprehensive check for LLM comparison data in multiple locations
        const hasLLMComparisonInRaw = this.checkForLLMComparisonData(rawOverviewData);
        const hasLLMComparisonInDistributed = this.checkForLLMComparisonData(distributedOverviewData);
        const hasLLMComparison = hasLLMComparisonInRaw || hasLLMComparisonInDistributed;
        
        console.log('🔍 Checking for LLM comparison data:', {
          hasLLMComparison,
          hasLLMComparisonInRaw,
          hasLLMComparisonInDistributed,
          rawOverviewKeys: rawOverviewData ? Object.keys(rawOverviewData) : [],
          distributedOverviewKeys: distributedOverviewData ? Object.keys(distributedOverviewData) : [],
          rawLLMComparisonExists: !!rawOverviewData?.llm_comparison,
          distributedLLMComparisonExists: !!distributedOverviewData?.llm_comparison
        });
        
        // Only start new calculation if:
        // 1. No LLM comparison data exists (in either location)
        // 2. No active task is running (check after a short delay to let resume check complete)
        setTimeout(() => {
          // Re-check distributed data one more time (in case it was set asynchronously)
          const finalCheckData = this.templateCommonService.getCurrentAnalysisResponse();
          const finalHasLLMComparison = this.checkForLLMComparisonData(finalCheckData) || hasLLMComparison;
          
          const hasActiveTask = this.templateCommonService.getCurrentLLMComparisonMetricsTaskId();
          if (!finalHasLLMComparison && !hasActiveTask && this.selectedEntities.length > 0) {
            console.log('🔄 LLM comparison metrics missing, triggering calculation in background...');
            // Get current filter data and ensure category/categories from version (history) are used for the payload
            const currentFilterData = this.templateCommonService.getCurrentFilterData();
            if (currentFilterData) {
              const filterDataForLLM: FilterData = {
                ...currentFilterData,
                category: (filters?.['category'] ?? currentFilterData.category) || 'General',
                categories: Array.isArray(filters?.['categories']) && (filters['categories'] as string[]).length > 0
                  ? (filters['categories'] as string[])
                  : (currentFilterData.categories ?? undefined),
              };
              // Trigger LLM comparison metrics calculation (non-blocking)
              this.templateCommonService.startLLMComparisonMetricsCalculation(
                filterDataForLLM,
                this.projectdataId || undefined,
                this.projectId || undefined
              );
            }
          } else if (finalHasLLMComparison) {
            console.log('✅ LLM comparison metrics already exist in projectdata, skipping calculation');
          } else if (hasActiveTask) {
            console.log('✅ LLM comparison metrics task already running, skipping new calculation');
          }
        }, 500);
        
        // Small delay to ensure data is distributed before tabs try to access it
        setTimeout(() => {
          this.isLoading = false;
          this.isHistoryLoading = false;
          this.hasSearched = true;
        }, 100);
      },
      error: (err) => {
        console.error('❌ Error loading project history:', err);
        this.error = err.error?.detail || 'Failed to load project history';
        this.isLoading = false;
        this.isHistoryLoading = false;
      }
    });
  }

  private setupFilterDataFromHistory(filters: any, versionData: any): void {
    // Extract filter information from history
    const country = filters.country || this.country || 'Global';
    const brands = filters.brands || [];
    const competitorsRaw = filters.competitors || [];
    let category = filters.category || this.category || 'General';
    let industry = filters.industry || 'General';
    // Competitors from history: may be string[] or { name: string, url?: string }[]
    const competitorNames = competitorsRaw.map((c: string | { name: string; url?: string }) =>
      typeof c === 'string' ? c : (c?.name ?? '')
    ).filter((n: string) => n);
    const competitorEntries = competitorsRaw.map((c: string | { name: string; url?: string }) =>
      typeof c === 'object' && c && 'name' in c ? c : null
    ).filter(Boolean) as { name: string; url?: string }[];

    // Set country if available
    if (filters.country) {
      this.country = filters.country;
    }
    
    // Set category if available
    if (filters.category) {
      this.category = filters.category;
    }
    
    // Build selected entities from brands and competitors (same shape as project-load path)
    const allBrands: string[] = [...brands];
    competitorNames.forEach((name: string) => {
      if (name && !allBrands.includes(name)) allBrands.push(name);
    });

    const projectCompetitors: { name: string; url?: string }[] = (this.projectData?.competitors || []).slice(0, 5);
    const brandDomain = (filters.description || filters.base_domain || '').trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '') || undefined;
    
    if (allBrands.length > 0) {
      this.selectedEntities = allBrands.map((name: string, index: number) => {
        const id = index === 0 && this.projectId ? parseInt(this.projectId, 10) || 1 : index + 1;
        if (index === 0) {
          return {
            id,
            name,
            country,
            domain: brandDomain || filters.description || undefined
          };
        }
        // Competitor: resolve url/domain from project.competitors (by name) or from history competitor entries
        const fromHistory = competitorEntries.find(ce => (ce.name || '').trim() === (name || '').trim());
        const fromProject = projectCompetitors.find(pc => (pc.name || '').trim() === (name || '').trim());
        const url = fromHistory?.url || fromProject?.url;
        const domain = url ? this.extractDomainFromCompetitorUrl(url) : undefined;
        return {
          id,
          name,
          country,
          domain: domain || undefined,
          ...(url && { url })
        };
      });
      
      this.preselectedBrand = brands[0] || allBrands[0];
    }
    
    // Create filter data object - include category/categories from project (for llm-ai-visibility payload)
    const filterData: FilterData = {
      selectedEntities: this.selectedEntities,
      category,
      categories: Array.isArray(filters.categories) ? filters.categories : undefined,
      industry,
      country,
      projectCreatedAt: this.projectData?.created_at || versionData.created_at,
      base_domain: filters.base_domain,
      description: filters.project_description
    };
    
    // Update filter data in service so tabs can access it
    this.templateCommonService.updateFilterData(filterData);
    
    console.log('✅ Filter data set from history:', filterData);
  }

  private loadProjectDataForHistory(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.projectId) {
        resolve();
        return;
      }

      // Load project data first (before history)
      this.projectManagementService.getProjectById(this.projectId).subscribe({
        next: (project: Project) => {
          this.projectData = project;
          
          // Set country from project
          if (project.primary_country) {
            this.country = project.primary_country;
          }
          
          // Set preselected brand from project
          this.preselectedBrand = project.name;
          
          // Update project data in service
          this.templateCommonService.updateProjectData(project);
          
          resolve();
        },
        error: (err) => {
          console.error('Failed to load project:', err);
          // Non-critical error - continue anyway
          resolve();
        }
      });
    });
  }

  /**
   * Check for LLM comparison data in overview data
   * Checks multiple possible locations where the data might be stored
   * Backend stores it as: overview.llm_comparison = { comparison_data: [...], metadata: {...} }
   */
  private checkForLLMComparisonData(overviewData: any): boolean {
    if (!overviewData) {
      return false;
    }

    // overview-prompts-build: treat as complete only when metrics are present (else allow repair task)
    if (overviewData.llm_comparison?.metadata?.source === 'overview_prompts_build') {
      const cd = overviewData.llm_comparison?.comparison_data;
      if (Array.isArray(cd) && cd.length > 0) {
        const firstItem = cd[0];
        if (firstItem && (firstItem.ai_visibility_metrics || firstItem.llm_metrics || firstItem.brand)) {
          return true;
        }
      }
    }

    // Check 1: overview.llm_comparison.comparison_data (most common location - where backend stores it)
    if (overviewData.llm_comparison?.comparison_data && 
        Array.isArray(overviewData.llm_comparison.comparison_data) && 
        overviewData.llm_comparison.comparison_data.length > 0) {
      // Also check if it has actual data (not just empty structure)
      const firstItem = overviewData.llm_comparison.comparison_data[0];
      if (firstItem && (firstItem.ai_visibility_metrics || firstItem.llm_metrics || firstItem.brand)) {
        console.log('✅ Found LLM comparison data at: overview.llm_comparison.comparison_data');
        return true;
      }
    }

    // Check 2: overview.structured_data.llm_comparison.comparison_data
    if (overviewData.structured_data?.llm_comparison?.comparison_data && 
        Array.isArray(overviewData.structured_data.llm_comparison.comparison_data) && 
        overviewData.structured_data.llm_comparison.comparison_data.length > 0) {
      const firstItem = overviewData.structured_data.llm_comparison.comparison_data[0];
      if (firstItem && (firstItem.ai_visibility_metrics || firstItem.llm_metrics || firstItem.brand)) {
        console.log('✅ Found LLM comparison data at: overview.structured_data.llm_comparison.comparison_data');
        return true;
      }
    }

    // Check 3: overview.comparison_data (direct)
    if (overviewData.comparison_data && 
        Array.isArray(overviewData.comparison_data) && 
        overviewData.comparison_data.length > 0) {
      const firstItem = overviewData.comparison_data[0];
      if (firstItem && (firstItem.ai_visibility_metrics || firstItem.llm_metrics || firstItem.brand)) {
        console.log('✅ Found LLM comparison data at: overview.comparison_data');
        return true;
      }
    }

    // Check 4: Check if ai_visibility_metrics exists directly in overview
    if (overviewData.ai_visibility_metrics) {
      console.log('✅ Found LLM comparison data at: overview.ai_visibility_metrics');
      return true;
    }

    return false;
  }

  private distributeHistoryDataToTabs(allTabsData: any, filters: any = {}): void {
    // Distribute data to appropriate tabs via service
    // Each tab component will pick up its data from the service
    // The data structure from history API is: { overview: {...}, 'ai-citations': {...}, ... }
    // overview-prompts-build bulk saves under data['overview-prompts-build'] — map to overview for the UI
    const opb = allTabsData?.['overview-prompts-build'];
    if (opb) {
      if (!allTabsData.overview) {
        allTabsData = { ...allTabsData, overview: opb };
      } else {
        allTabsData = {
          ...allTabsData,
          overview: {
            ...opb,
            ...allTabsData.overview,
            structured_data: {
              ...(opb.structured_data || {}),
              ...(allTabsData.overview.structured_data || {}),
            },
            llm_comparison:
              allTabsData.overview.llm_comparison?.comparison_data?.length > 0
                ? allTabsData.overview.llm_comparison
                : opb.llm_comparison ?? allTabsData.overview.llm_comparison,
          },
        };
      }
    }
    if (allTabsData?.prompts && allTabsData?.overview && typeof allTabsData.overview === 'object' && !allTabsData.overview.prompts) {
      allTabsData = {
        ...allTabsData,
        overview: {
          ...allTabsData.overview,
          prompts: allTabsData.prompts
        }
      };
    }

    console.log('📦 Distributing history data to tabs:', Object.keys(allTabsData));

    /** Build context.citations and context.citations_table from overview discovery_sections (for AI-Citations when only phase-1 data). */
    const buildAiCitationsContextFromDiscovery = (ds: Record<string, unknown> | null | undefined): { citations: any[]; citations_table: any[] } => {
      const citations: any[] = [];
      const domainSet = new Set<string>();
      if (!ds || typeof ds !== 'object' || Array.isArray(ds)) return { citations, citations_table: [] };
      const sectionOrder = ['primary_branded', 'generic', 'competitors'] as const;
      for (const section of sectionOrder) {
        const raw = ds[section];
        // Backend: primary_branded/generic are arrays; competitors is object (brand -> list of rows)
        const rows: any[] = Array.isArray(raw)
          ? raw
          : raw && typeof raw === 'object' && !Array.isArray(raw)
            ? (Object.values(raw) as any[]).flat()
            : [];
        for (const row of rows) {
          if (!row || typeof row !== 'object') continue;
          const citeList = Array.isArray(row.citations) ? row.citations : [];
          for (const c of citeList) {
            if (c?.url || c?.title) {
              citations.push({ url: c.url, title: c.title || c.url, snippet: c.snippet, source: 'AI Visibility', confidence: 0.8 });
              try {
                const u = (c.url || '').trim();
                const href = u.startsWith('http') ? u : 'https://' + u;
                const host = new URL(href).hostname || u.split('/')[0] || '';
                if (host) domainSet.add(host);
              } catch {
                const h = (c.url || '').replace(/^https?:\/\//, '').split('/')[0];
                if (h) domainSet.add(h);
              }
            }
          }
        }
      }
      const citations_table = Array.from(domainSet).map(domain => ({ domain, category: 'Other', authority_score: 0 }));
      return { citations, citations_table };
    };
    
    // Helper function to extract the correct data structure
    const extractTabData = (tabData: any, tabName?: string): any => {
      if (!tabData) return null;
      
      // Check for error in metadata (response field removed from backend)
      if (tabData.metadata && tabData.metadata.llm_error) {
        console.warn(`⚠️ ${tabName || 'Tab'}: LLM returned error, returning null`);
        return null;
      }
      
      let extractedData: any = null;
      
      // If structured_data exists, use it (preferred)
      if (tabData.structured_data) {
        extractedData = tabData.structured_data;
      }
      // Legacy support: if response field exists (old cached data), try to parse it
      else if (tabData.response && typeof tabData.response === 'string' && tabData.response.trim().startsWith('{')) {
        try {
          extractedData = JSON.parse(tabData.response);
        } catch (e) {
          console.warn(`⚠️ ${tabName || 'Tab'}: Failed to parse response JSON:`, e);
          return null;
        }
      }
      // Otherwise, use the full object (tabs should handle it)
      else {
        extractedData = tabData;
      }
      
      // For overview tab, merge root-level metadata if it exists and structured_data doesn't have it
      if (tabName === 'overview' && tabData && extractedData) {
        // When overview was stored with structured_data, chart data may live at tab root; merge so Business Performance chart shows
        if (tabData.competitor_analysis != null && extractedData.competitor_analysis == null) {
          extractedData.competitor_analysis = tabData.competitor_analysis;
        }
        if (tabData.performance_table != null && extractedData.performance_table == null) {
          extractedData.performance_table = tabData.performance_table;
        }
        if (tabData.composite_score != null && extractedData.composite_score == null) {
          extractedData.composite_score = tabData.composite_score;
        }
        if (tabData.metadata) {
        if (!extractedData.metadata) {
          extractedData.metadata = tabData.metadata;
        } else {
          // Merge metadata, with structured_data taking precedence
          extractedData.metadata = { ...tabData.metadata, ...extractedData.metadata };
          }
        }

        // Also propagate LLM comparison data from root-level into extracted overview data
        // Backend stores it as: overview.llm_comparison = { comparison_data: [...], metadata: {...} }
        // Always use root-level llm_comparison (backend stores it there, not in structured_data)
        if (tabData.llm_comparison) {
          extractedData.llm_comparison = tabData.llm_comparison;
        }
        if (tabData.overview_prompt_collection) {
          extractedData.overview_prompt_collection = tabData.overview_prompt_collection;
        }
        if (tabData.prompts) {
          extractedData.prompts = tabData.prompts;
        }
        // When overview has only llm_comparison (no bulk-search overview structured_data),
        // synthesize composite_score, performance_table, and metadata so Business Overview and chart render
        if (!extractedData.composite_score || !extractedData.performance_table) {
          const llm = extractedData.llm_comparison || tabData.llm_comparison;
          const comparisonData = llm?.comparison_data;
          const brands = filters?.brands || [];
          const primaryBrand = brands[0] || 'Your Brand';
          let compositeValue = 0;
          const performanceRows: any[] = [];
          if (Array.isArray(comparisonData) && comparisonData.length > 0) {
            comparisonData.forEach((item: any, idx: number) => {
              const brand = item.brand || brands[idx] || `Brand ${idx + 1}`;
              const metrics = item.ai_visibility_metrics || item.llm_metrics || {};
              const vals = [
                metrics.ai_visibility,
                metrics.visibility_score,
                metrics.chatgpt?.ai_visibility,
                metrics.gemini?.ai_visibility,
                metrics.ai_overview?.ai_visibility,
                metrics.ai_mode?.ai_visibility
              ].filter((v): v is number => typeof v === 'number');
              const score = vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
              if (idx === 0) compositeValue = score;
              performanceRows.push({
                brand,
                is_your_brand: idx === 0,
                brand_reputation: score,
                market_presence: score,
                customer_satisfaction: score,
                innovation_score: score,
                financial_performance: score,
                industry_influence: score,
                market_share: 'N/A',
                ranking: 'N/A'
              });
            });
          }
          if (!extractedData.composite_score) {
            extractedData.composite_score = {
              value: compositeValue,
              label: compositeValue >= 80 ? 'Very Good' : compositeValue >= 60 ? 'Good' : compositeValue >= 40 ? 'Average' : 'Needs Improvement',
              color: compositeValue >= 80 ? '#4caf50' : compositeValue >= 60 ? '#ff9800' : compositeValue >= 40 ? '#2196f3' : '#f44336',
              brand: primaryBrand
            };
          }
          if (!extractedData.performance_table) {
            extractedData.performance_table = performanceRows.length > 0
              ? performanceRows
              : [{ brand: primaryBrand, is_your_brand: true, brand_reputation: compositeValue, market_presence: compositeValue, customer_satisfaction: compositeValue, innovation_score: compositeValue, financial_performance: compositeValue, industry_influence: compositeValue, market_share: 'N/A', ranking: 'N/A' }];
          }
          if (!extractedData.metadata) {
            extractedData.metadata = {};
          }
          extractedData.metadata.industry = extractedData.metadata.industry || filters.industry || 'N/A';
          extractedData.metadata.your_brand = extractedData.metadata.your_brand || primaryBrand;
          extractedData.metadata.competitors = extractedData.metadata.competitors ?? brands.slice(1) ?? [];
          extractedData.metadata.brands_analyzed = extractedData.metadata.brands_analyzed ?? (performanceRows.length || 1);
          extractedData.metadata.data_source = extractedData.metadata.data_source || 'AI Visibility';
        }
      }
      
      return extractedData;
    };
    
    // Set overview data
    if (allTabsData.overview) {
      const overviewData = extractTabData(allTabsData.overview, 'overview');
      console.log('✅ Setting overview data', overviewData);
      this.templateCommonService.updateAnalysisResponse(overviewData);
    }
    
    // AI-Citations: use tab payload when present; when only phase-1 data (overview-prompts-build, category-analysis, ai-visibility), build from overview
    const overview = allTabsData.overview || allTabsData['overview-prompts-build'];
    const comparisonData = overview?.llm_comparison?.comparison_data as any[] | undefined;
    const discoverySections = comparisonData?.[0]?.ai_visibility_metrics?.discovery_sections as Record<string, any[]> | undefined;

    if (allTabsData['ai-citations']) {
      const aiCitationsData = extractTabData(allTabsData['ai-citations']);
      // Reuse overview.llm_comparison so AI Cited domains / Sources by domain work from new flow
      if (overview?.llm_comparison) {
        aiCitationsData.llm_comparison = overview.llm_comparison;
        if (comparisonData?.length && (!aiCitationsData.universities || aiCitationsData.universities.length === 0)) {
          aiCitationsData.universities = comparisonData.map((item: any) => ({
            name: item.brand || 'Brand',
            citation_frequency: 0,
            citation_share_of_voice: 0,
            authority_weight: 0,
            relevance_score: 0,
            ai_readability: 0,
            external_mentions: 0,
            diversity_index: 0,
            persistence_score: 0
          }));
        }
        if (discoverySections) {
          const { citations, citations_table } = buildAiCitationsContextFromDiscovery(discoverySections);
          aiCitationsData.context = aiCitationsData.context || {};
          if (citations.length) aiCitationsData.context.citations = citations;
          if (citations_table.length && !(aiCitationsData.context.citations_table?.length)) aiCitationsData.context.citations_table = citations_table;
        }
      }
      console.log('✅ Setting ai-citations data', aiCitationsData);
      this.templateCommonService.updateAiCitationsResponse(aiCitationsData);
    } else if (overview?.llm_comparison && (comparisonData?.length || discoverySections)) {
      // Phase-1 only: no ai-citations key; build from overview so AI-Citations tab works
      const aiCitationsData: any = {
        brands: [],
        context: { citations: [], citations_table: [] },
        timestamp: new Date().toISOString(),
        model_meta: {},
        llm_comparison: overview.llm_comparison,
        universities: comparisonData?.length ? comparisonData.map((item: any) => ({
          name: item.brand || 'Brand',
          citation_frequency: 0,
          citation_share_of_voice: 0,
          authority_weight: 0,
          relevance_score: 0,
          ai_readability: 0,
          external_mentions: 0,
          diversity_index: 0,
          persistence_score: 0
        })) : []
      };
      if (discoverySections) {
        const { citations, citations_table } = buildAiCitationsContextFromDiscovery(discoverySections);
        aiCitationsData.context.citations = citations;
        aiCitationsData.context.citations_table = citations_table;
      }
      console.log('✅ Setting ai-citations data (from overview, phase-1 only)', aiCitationsData);
      this.templateCommonService.updateAiCitationsResponse(aiCitationsData);
    }
    
    if (allTabsData['category-analysis']) {
      // Category Analysis is calculated in AI Visibility; bulk returns minimal. Do not overwrite with minimal payload.
      const categoryTab = allTabsData['category-analysis'];
      const sd = categoryTab?.structured_data || categoryTab;
      const hasData = (sd?.brands?.length > 0) || (sd?.growing_prompts && Object.keys(sd.growing_prompts).length > 0);
      if (hasData) {
        console.log('✅ Setting category-analysis data (full tab for new flow)', categoryTab);
        this.templateCommonService.updateCategoryAnalysisResponse(categoryTab);
      } else {
        console.log(
          '⏭️ Category-analysis payload minimal after overview-first; scheduling full category tab load'
        );
        this.templateCommonService.scheduleCategoryAnalysisBackfillAfterMinimalBulk();
      }
    }
    
    if (allTabsData['sentiment-tracking']) {
      const sentimentData = extractTabData(allTabsData['sentiment-tracking']);
      console.log('✅ Setting sentiment-tracking data', sentimentData);
      this.templateCommonService.updateSentimentTrackingResponse(sentimentData);
    }
    
    if (allTabsData['ai-content-index']) {
      const aiContentData = extractTabData(allTabsData['ai-content-index']);
      console.log('✅ Setting ai-content-index data', aiContentData);
      this.templateCommonService.updateAiContentIndexResponse(aiContentData);
    }
    
    if (allTabsData['content-creation']) {
      const contentCreationData = extractTabData(allTabsData['content-creation']);
      console.log('✅ Setting content-creation data', contentCreationData);
      this.templateCommonService.updateContentCreationResponse(contentCreationData);
    }
    
    if (allTabsData['performance-tracking']) {
      const performanceData = extractTabData(allTabsData['performance-tracking'], 'performance-tracking');
      console.log('✅ Setting performance-tracking data', performanceData);
      if (performanceData) {
        this.templateCommonService.updatePerformanceTrackingResponse(performanceData);
      } else {
        console.warn('⚠️ Performance-tracking data is null or invalid, skipping update');
      }
    }
    
    if (allTabsData['consumer-insights']) {
      const consumerInsightsData = extractTabData(allTabsData['consumer-insights']);
      console.log('✅ Setting consumer-insights data', consumerInsightsData);
      this.templateCommonService.updateConsumerInsightsResponse(consumerInsightsData);
    }
    
    if (allTabsData['eeat-audit']) {
      const eeatTabData = allTabsData['eeat-audit'];
      // EEAT audit data structure: { structured_data: { brands: [...] }, metadata: {...}, ... }
      // The actual result is in structured_data
      let eeatAuditData = null;
      if (eeatTabData.structured_data) {
        eeatAuditData = eeatTabData.structured_data;
      } else {
        // Fallback: use extractTabData for legacy format
        eeatAuditData = extractTabData(eeatTabData, 'eeat-audit');
      }
      console.log('✅ Setting eeat-audit data', eeatAuditData);
      this.templateCommonService.updateEeatAuditResponse(eeatAuditData);
    }
    
    if (allTabsData['landing-page-audit']) {
      const landingPageTabData = allTabsData['landing-page-audit'];
      // Landing Page audit data structure: { structured_data: { composite_score, audit_areas: {...}, url }, metadata: {...}, ... }
      // Pass the full response (including structured_data) to match bulk-search format
      console.log('✅ Setting landing-page-audit data', landingPageTabData);
      this.templateCommonService.updateLandingPageAuditResponse(landingPageTabData);
    }
    
    if (allTabsData['ai-audit']) {
      const aiAuditTabData = allTabsData['ai-audit'];
      // AI Audit data structure: { structured_data: { brand, url, composite_score, audit_areas: {...}, summary }, metadata: {...}, ... }
      // Pass the full response (including structured_data) to match bulk-search format
      console.log('✅ Setting ai-audit data', aiAuditTabData);
      this.templateCommonService.updateAIAuditResponse(aiAuditTabData);
    }
    
    console.log('✅ Distributed history data to all tabs');
  }

  /** Normalize URL for display: strip protocol, optional trailing slash (e.g. "https://www.carwale.com/bmw/" -> "www.carwale.com/bmw") */
  private normalizeUrlForDisplay(url: string): string {
    if (!url || !url.trim()) return '';
    let u = url.trim().replace(/^https?:\/\//i, '');
    u = u.replace(/\/$/, '');
    return u;
  }

  private autoTriggerAnalysisWithProject(project: Project): void {
    // Use selectedEntities already set from project (primary + project.competitors) so API gets competitors
    const selectedEntities = this.selectedEntities.length > 0
      ? this.selectedEntities
      : [{ id: 1, name: project.name, country: this.country, domain: project.description }];
    const filterData: FilterData = {
      selectedEntities,
      category: this.category,
      country: this.country,
      projectCreatedAt: project.created_at,
      hardReload: this.hardReload || false
    };

    this.selectedEntities = selectedEntities;

    // Update filter data in service and trigger analysis with bulk search
    this.templateCommonService.updateFilterData(filterData);
    this.templateCommonService.triggerAnalysis(
      filterData, 
      true, 
      this.projectId ? this.projectId : undefined, 
      this.projectdataId ? this.projectdataId : undefined
    ); // Pass project info
    
    // Mark that we have performed a search
    this.hasSearched = true;
  }

  private loadInitialData(): void {
    // // console.log("loadInitialData");

    // Load country options from service first (shared service ensures sync)
    const countrySub = this.countryService.getCountryOptions().subscribe({
      next: (countries) => {
        this.filterConfig.countryOptions = countries;
        // // console.log("Filter config updated with countries:", this.filterConfig);
      },
      error: (err) => {
        console.error('Error loading country options:', err);
        // Fallback to default countries
        this.filterConfig.countryOptions = this.countryService.getDefaultCountries();
      }
    });

    this.subscriptions.push(countrySub);
  }



  onAnalyzeClick(filterData: FilterData): void {
    // console.log('Generic Template AI - Analyze clicked:', filterData);
    
    // Set bulk analysis flag
    this.isBulkAnalysisInProgress = true;
    
    // If user is not on overview tab, switch to overview tab FIRST
    if (this.currentActiveTab !== 'overview') {
      console.log(`🔄 Analysis clicked from ${this.currentActiveTab} tab, switching to overview first`);
      this.switchToOverviewTab();
      
      // Wait a moment for tab switch to complete, then start bulk search
      setTimeout(() => {
        this.startBulkSearch(filterData);
      }, 100);
    } else {
      // Already on overview tab, start bulk search immediately
      this.startBulkSearch(filterData);
    }
  }

  private startBulkSearch(filterData: FilterData): void {
    const validationError = this.validatePrimaryBrandSelection(filterData);
    if (validationError) {
      this.templateCommonService.setError(validationError);
      this.isBulkAnalysisInProgress = false;
      return;
    }

    console.log('🚀 Starting bulk search analysis');
    
    // Update filter data in service and trigger analysis with bulk search
    this.templateCommonService.updateFilterData(filterData);
    // Pass project info for saving to projectdata
    this.templateCommonService.triggerAnalysis(
      filterData, 
      true, 
      this.projectId ? this.projectId : undefined, 
      this.projectdataId ? this.projectdataId : undefined
    );
    // console.log('Generic Template AI - Analysis triggered for overview tab');
  }

  // Tab event handlers
  onTabChange(tabId: string): void {
    console.log('Tab changed to:', tabId);
    
    // Update current active tab
    this.currentActiveTab = tabId;
    // Tab switch is intentionally API-free.
    // While background analysis runs, tab content should only reflect loader/progress state.
  }

  onReloadTab(tabId: string): void {
    // Reload button on each tab: call bulk-search for this tab only and update projectdata
    if (this.isBulkAnalysisInProgress || this.templateCommonService.getCurrentTaskId()) {
      this.templateCommonService.setError('Analysis is already running in background. Please wait for it to complete.');
      return;
    }

    if (this.selectedEntities.length > 0) {
      const filterData: FilterData = {
        selectedEntities: this.selectedEntities,
        category: this.category,
        country: this.country
      };
      const validationError = this.validatePrimaryBrandSelection(filterData);
      if (validationError) {
        this.templateCommonService.setError(validationError);
        return;
      }
      this.templateCommonService.updateFilterData(filterData);
      this.templateCommonService.triggerTabReload(tabId, true);
    }
  }

  onVisualizationChange(event: { tabId: string, type: string }): void {
    // // console.log('Visualization changed:', event);
    // Handle visualization change
  }

  // Switch to overview tab programmatically
  private switchToOverviewTab(): void {
    console.log('🔄 Switching to overview tab via common-tabs component');
    
    // Update current active tab
    this.currentActiveTab = 'overview';
    
    // Directly call the common-tabs component to switch UI, skip analysis since bulk analysis is in progress
    if (this.commonTabsComponent) {
      this.commonTabsComponent.openTab('overview', true); // Skip analysis
    } else {
      console.warn('Common tabs component not available, falling back to onTabChange');
      this.onTabChange('overview');
    }
  }

  // Refresh current tab after analysis completes
  private refreshCurrentTabAfterAnalysis(): void {
    if (this.currentActiveTab && this.currentActiveTab !== 'overview') {
      console.log(`🔄 Refreshing ${this.currentActiveTab} tab after analysis completion`);
      // Trigger tab reload for the current active tab from backend
      this.templateCommonService.triggerTabReload(this.currentActiveTab, true);
    }
  }

  // Update selected entities when filter changes
  onFilterChange(filterData: FilterData): void {
    // console.log('Filter changed:', filterData);
    // Ignore transient empty emissions from child filter during initialization,
    // so project-seeded brand/competitor entities are not accidentally cleared.
    if (
      this.selectedEntities.length > 0 &&
      (!filterData.selectedEntities || filterData.selectedEntities.length === 0)
    ) {
      this.country = filterData.country;
      this.hardReload = filterData.hardReload || false;
      this.templateCommonService.handleFilterChange({
        ...filterData,
        selectedEntities: this.selectedEntities
      });
      return;
    }

    // Update selected entities and category/country for tabs
    this.selectedEntities = filterData.selectedEntities;
    this.country = filterData.country;

    // Store hardReload flag for use in tab analysis
    this.hardReload = filterData.hardReload || false;

    // Update the template common service with the new filter data
    this.templateCommonService.handleFilterChange(filterData);

    // Mark that user has performed a search when entities are selected
    if (filterData.selectedEntities.length > 0) {
      this.hasSearched = true;
    }
  }

  private validatePrimaryBrandSelection(filterData: FilterData): string | null {
    const entities = filterData?.selectedEntities || [];
    if (entities.length === 0) {
      return 'Primary brand is required. Please select a brand before analysis.';
    }

    const primaryBrand = (entities[0]?.name || '').trim();
    if (!primaryBrand) {
      return 'Primary brand is required. Please select a brand before analysis.';
    }

    if (this.invalidPrimaryBrandNames.has(primaryBrand.toLowerCase())) {
      return 'Invalid primary brand selected. Please choose a valid project brand.';
    }

    return null;
  }

  /** Temporary behavior: hard refresh once after phase-2 background tabs finish. */
  private triggerPostBackgroundBulkReload(): void {
    if (this.hasTriggeredPostBackgroundReload) return;
    this.hasTriggeredPostBackgroundReload = true;
    setTimeout(() => {
      window.location.reload();
    }, 400);
  }

  /** Extract display domain from competitor URL for tooltips (e.g. https://www.example.co.uk -> example.co.uk). */
  private extractDomainFromCompetitorUrl(url: string | undefined): string | undefined {
    if (!url || typeof url !== 'string') return undefined;
    try {
      const href = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
      const host = new URL(href).hostname || '';
      return host.replace(/^www\./, '') || undefined;
    } catch {
      return undefined;
    }
  }
}
